import { useCookies } from "react-cookie";
import Details from "./details";
import { useEffect, useReducer, useState } from "react";
import TagManager from "react-gtm-module";
import { useEffectOnce } from "react-use";

const tokenName = "_skg_r_cookieconsent";

function addToDataLayer(...args) {
    args.forEach(arg => {
        TagManager.dataLayer({
            dataLayer: arg
        })
    })
}

function denyAndWait() {
    addToDataLayer(
        [
            "consent",
            "default",
            {
                ad_storage: "denied",
                analytics_storage: "denied",
                ad_user_data: "denied",
                ad_personalization: "denied",
                wait_for_update: 500
            }
        ]
    )
}

function allowMarketing() {
    addToDataLayer(
        [
            "consent",
            "update",
            {
                "ad_storage": "granted",
                "ad_user_data": "granted",
                "ad_personalization": "granted"
            }
        ],
        {
            "event": "consent_marketing",
            "consent_marketing": true
        }
    )
}

function denyMarketing() {
    addToDataLayer(
        [
            "consent",
            "update",
            {
                'ad_storage': "denied",
                "ad_user_data": "denied",
                "ad_personalization": "denied"
            }
        ],
        [
            "set",
            "ads_data_redaction",
            true
        ],
        {
            "event": "consent_marketing",
            "consent_marketing": false
        }
    )
}

function allowStatistics() {
    addToDataLayer(
        [
            "consent",
            "update",
            {
                "analytics_storage": "granted"
            }
        ],
        {
            "event": "consent_statistics",
            "consent_statistics": true
        }
    )
}

function denyStatistics() {
    addToDataLayer(
        [
            "consent",
            "update",
            {
                "analytics_storage": "denied"
            }
        ],
        {
            "event": "consent_statistics",
            "consent_statistics": false
        }
    )
}

function compose(...args) {
    return function (initial = undefined) {
        return args.reduce((acc, cur) => {
            return cur(acc)
        }, initial)
    }
}

function once(fn) {
    let hasBeenCalled = false
    return (...args) => {
        if (hasBeenCalled) {
            return false
        }
        hasBeenCalled = true
        return fn(...args)
    }
}

const denyAndWaitOnce = once(denyAndWait)
const denyAllOnce = once(compose(denyMarketing, denyStatistics))
const allowAllOnce = once(compose(allowStatistics, allowMarketing))
const allowMarketingOnce = once(compose(allowMarketing, denyStatistics))
const allowStatisticsOnce = once(compose(allowStatistics, denyMarketing))

const CookieConsent = () => {
    const [isOpen, setIsOpen] = useState(false)
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [cookies, setCookie] = useCookies([tokenName])

    const [state, dispatch] = useReducer((state, action) => {
        return { ...state, ...action }
    }, {
        marketing: false,
        statistics: false,
        required: true
    })

    const toggle = () => setDetailsOpen(p => !p)

    const acceptRequired = () => {
        setCookie(tokenName, ["required"])
        denyMarketing()
        denyStatistics()
    }

    const acceptAll = () => {
        setCookie(tokenName, ["required", "marketing", "statistics"])
        allowMarketing()
        allowStatistics()
    }

    const acceptSelected = () => {
        let cookieContent = [];
        for (const key in state) {
            if (state[key]) {
                cookieContent = [...cookieContent, key];
            }
        }
        setCookie(tokenName, cookieContent);
        state.marketing ? allowMarketing() : denyMarketing()
        state.statistics ? allowStatistics() : denyStatistics()
    }

    useEffectOnce(() => {
        denyAndWaitOnce()
        if (tokenName in cookies) {
            const cookie = cookies[tokenName]
            if (cookie.includes("marketing") && cookie.includes("statistics")) {
                allowAllOnce()
            }
            else if (cookie.includes("statistics")) {
                allowStatisticsOnce()
            }
            else if (cookie.includes("marketing")) {
                allowMarketingOnce()
            }
            else {
                denyAllOnce()
            }
        }
    }, [cookies])

    useEffect(() => {
        setIsOpen(!(tokenName in cookies))
    }, [cookies])

    if (!isOpen) return null;

    return (
        <div
            className="absolute bottom-0 right-0 left-0 bg-[rgba(0,0,0,.5)] top-0 z-50 font-supermolot"
        >
            <div
                className="absolute bottom-0 right-0 left-0 bg-white z-50 font-supermolot"
            >
                <div
                    className="max-w-1400 mx-auto p-5 space-y-4"
                >
                    <h2 className="text-2xl">Denne hjemmeside har cookies</h2>
                    <p className="text-sm">
                        Skorstensgaard.dk bruger cookies til optimering af brugervenlighed, besøgsstatistik, sikkerhed og adgang til funktioner på sociale medier.
                    </p>
                    <p className="text-sm"                                                                                                                            >
                        Du kan altid ændre eller tilbagetrække dit samtykke her eller ved at klikke på "Cookies" under fanen "Om os" på toppen af siden. Du kan desuden se, hvordan vi bruger disse cookies ved at klikke "Vis detaljer" herunder.
                    </p>
                    <div className="space-x-4 flex flex-row items-center">
                        <div className="flex items-center space-x-1">
                            <label htmlFor="required">Nødvendig</label>
                            <input
                                type="checkbox"
                                id="required"
                                checked={state.required}
                            />
                        </div>
                        <div className="flex items-center space-x-1">
                            <label htmlFor="statistics">Statistik</label>
                            <input
                                type="checkbox"
                                id="statistics"
                                checked={state.statistics}
                                onChange={e => dispatch({ statistics: e.target.checked })}
                            />
                        </div>
                        <div className="flex items-center space-x-1">
                            <label htmlFor="marketing">Marketing</label>
                            <input
                                type="checkbox"
                                id="marketing"
                                checked={state.marketing}
                                onChange={e => dispatch({ marketing: e.target.checked })}
                            />
                        </div>
                        <button
                            className="bg-skgBlue px-3 py-2 text-white text-sm"
                            onClick={toggle}>Vis detaljer</button>
                    </div>
                    <div className="space-y-3 md:space-y-0 md:space-x-3 flex flex-wrap">
                        <button onClick={acceptRequired} className="bg-skgBlue px-3 py-2 text-white font-bold basis-full md:basis-auto">Kun nødvendige</button>
                        <button onClick={acceptSelected} className="bg-skgBlue px-3 py-2 text-white font-bold basis-full md:basis-auto">Tillad valgte</button>
                        <button onClick={acceptAll} className="bg-skgBlue px-3 py-2 text-white font-bold basis-full md:basis-auto">Tillad alle cookies</button>
                    </div>
                    <Details
                        show={detailsOpen}
                    />
                </div>
            </div>
        </div>
    )
}


export default CookieConsent